import { apiUrls } from '@/constants/apiUrls';
import { QueryKeys } from '@/constants/queryKeys';
import { useConfig } from '@/hooks/useConfig';
import { useMetrics } from '@/hooks/useMetrics';
import { TYPOGRAPHY } from '@/pages/ListContentPage/constants';
import { cardHoverSx, getCardSx } from '@/theme/utils';
import { get } from '@/services/api';
import { Box, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Award, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const CertificatesSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const metrics = useMetrics();
  const config = useConfig();

  const getEarnedCertificates = async () => {
    const response = await get(apiUrls.home.stats + '?action=certificates');
    const data = response.data;
    return data;
  };
  const earnedCertificates = useQuery({
    queryKey: [QueryKeys.HOME_STATS, 'earned_certificates'],
    queryFn: getEarnedCertificates,
  });

  const handleClick = () => {
    navigate('/certificate');
    metrics.track({
      action: 'click',
      extra_data: { button: 'navigate-to-certificate', path: '/certificate' },
    });
    metrics.trackEvent({
      action: 'shortcut_clicked',
      section: 'home',
      companies: config.slug_name ? [config.slug_name] : undefined,
      extra_data: { destination: 'certificates' },
    });
  };

  const isPlural = earnedCertificates.data?.earned_certificates > 1;
  const getTitleText = () => {
    if (isPlural) {
      return Boolean(config.certs_section)
        ? t('homeScreen.earnedCertificatesCustom').replace(
            '{{certificates}}',
            config.certs_section,
          )
        : t('homeScreen.earnedCertificate');
    }

    return Boolean(config.certs_section)
      ? t('homeScreen.earnedCertificateCustom').replace(
          '{{certificate}}',
          config.certs_section,
        )
      : t('homeScreen.earnedCertificate');
  };

  if (earnedCertificates.isLoading) return <></>;

  const count = earnedCertificates?.data?.earned_certificates ?? 0;
  const countLabel = earnedCertificates.isPending ? '-' : count;

  return (
    <Box
      component="section"
      onClick={handleClick}
      sx={{
        ...getCardSx(theme),
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, md: 1.75 },
        padding: { xs: '11px 14px', md: '18px' },
        '&:hover': cardHoverSx,
      }}
    >
      {/* Icon box */}
      <Box
        sx={{
          width: { xs: 44, md: 48 },
          height: { xs: 44, md: 48 },
          borderRadius: { xs: '10px', md: '12px' },
          backgroundColor: `${theme.palette.primary.main}14`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Award size={22} color={theme.palette.primary.main} />
      </Box>

      {/* Text */}
      <Box flex={1} minWidth={0}>
        <Box
          sx={{
            fontSize: { xs: '14px', md: '15px' },
            fontFamily: TYPOGRAPHY.bold,
            fontWeight: 700,
            color: '#1F1333',
            lineHeight: 1.3,
          }}
        >
          {t('homeScreen.myCertificatesWidget')}
        </Box>
        <Box
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: '#8C8494',
            fontFamily: TYPOGRAPHY.regular,
            lineHeight: 1.3,
            mt: 0.25,
          }}
        >
          {`${countLabel} ${t('homeScreen.obtained')}`}
        </Box>
      </Box>

      <ChevronRight size={18} color="#8C8494" />
    </Box>
  );
};
